import {
  getWorkTimeChartDurations,
  WORK_TIME_CHART_SERIES,
  WorkTimeChartSeriesKey
} from '@app/shared/components/reports/time-report-panel-chart/services/work-time-chart-durations';
import {
  GroupingModeEnum,
  SelectedTimeReportTypeEnum,
  TimeReportSessionTypeEnum
} from '@app/shared/types/models/reports/time-report-panel.model';
import { TimeReportRowVM } from '@app/shared/types/models/reports/time-report-row.model';
import { isWorkTimeReport, WorkTimeReport } from '@app/shared/types/models/reports/work-time-report';
import moment from 'moment';
import {
  groupTimeReportRows,
  isWorkTimeOnlyGrouping,
  MISSING_GROUP_KEY,
  TimeReportRowGroup
} from './time-report-row.grouping';

export const EMPTY_CHART_LABEL_KEY = 'task-filters.emptyValue';
export const CHART_ABSENCE_SERIES_KEY = 'time-reports-filters.kind-absence';
export const CHART_WORK_TIME_STACK = 'workTime';
export const CHART_ABSENCE_STACK = 'absence';

export const CHART_HOURS_TYPE_SERIES_ORDER = WORK_TIME_CHART_SERIES;
export type ChartHoursTypeSeriesKey = WorkTimeChartSeriesKey;

export interface TimeReportChartBucket {
  key: string;
  label: string;
  labelTranslationKey: string | undefined;
  workTimeByHoursType: Partial<Record<ChartHoursTypeSeriesKey, number>>;
  absenceMinutes: number;
}

export interface TimeReportChartCalendar {
  dates: Date[];
  labels: string[];
  dateComparison: 'day' | 'month';
}

export interface TimeReportWorkTimeChartSeries {
  hoursType: ChartHoursTypeSeriesKey;
  label: string;
  labelKey: string;
  hours: number[];
}

export interface TimeReportMixedChartView {
  labels: string[];
  workTimeSeries: TimeReportWorkTimeChartSeries[];
  absenceHours: number[];
  absenceSeriesLabel: string;
  includeAbsence: boolean;
}

export function durationToMinutes(duration: string | undefined): number {
  if (!duration) {
    return 0;
  }
  const minutes = moment.duration(duration).asMinutes();
  return Number.isFinite(minutes) ? minutes : 0;
}

export function minutesToHours(minutes: number): number {
  return minutes / 60;
}

export function usesLegacyWorkTimeRangeChart(mode: SelectedTimeReportTypeEnum, rows: TimeReportRowVM[]): boolean {
  return mode === SelectedTimeReportTypeEnum.RANGE && !rows.some(row => row.kind === 'Absence');
}

export function workTimeReportsFromChartRows(rows: TimeReportRowVM[]): WorkTimeReport[] {
  const reports: WorkTimeReport[] = [];
  for (const row of rows) {
    if (row.kind !== 'WorkTime' || !isWorkTimeReport(row.source)) {
      continue;
    }
    reports.push(row.source);
  }
  return reports;
}

export function usesCategoryChartAxis(mode: GroupingModeEnum): boolean {
  return (
    mode === GroupingModeEnum.USER ||
    mode === GroupingModeEnum.USER_DATE ||
    mode === GroupingModeEnum.COMPANY ||
    mode === GroupingModeEnum.TYPE ||
    mode === GroupingModeEnum.PROJECT ||
    mode === GroupingModeEnum.TASK
  );
}

export function hoursTypeLabelKey(hoursType: ChartHoursTypeSeriesKey): string {
  switch (hoursType) {
    case TimeReportSessionTypeEnum.STANDARD:
      return 'hours-type-abbr-OrdinaryHours';
    case TimeReportSessionTypeEnum.BANK_PLUS:
      return 'hours-type-abbr-BankPlus';
    case TimeReportSessionTypeEnum.OVERTIME_50:
      return 'hours-type-abbr-Overtime50';
    case TimeReportSessionTypeEnum.OVERTIME_100:
      return 'hours-type-abbr-Overtime100';
  }
}

export function aggregateTimeReportChartBuckets(
  rows: TimeReportRowVM[],
  groupingMode: GroupingModeEnum,
  calendar?: TimeReportChartCalendar
): TimeReportChartBucket[] {
  if (usesCategoryChartAxis(groupingMode)) {
    return groupTimeReportRows(rows, groupingMode).map(groupToBucket);
  }

  if (calendar && calendar.dates.length > 0) {
    const calendarRows = isWorkTimeOnlyGrouping(groupingMode) ? rows.filter(row => row.kind === 'WorkTime') : rows;
    return aggregateByCalendar(calendarRows, calendar);
  }

  return groupTimeReportRows(rows, GroupingModeEnum.DATE).map(groupToBucket);
}

export function buildMixedChartView(
  buckets: TimeReportChartBucket[],
  translate: (key: string) => string
): TimeReportMixedChartView {
  const hoursTypes = collectPresentHoursTypes(buckets);
  const includeAbsence = buckets.some(bucket => bucket.absenceMinutes > 0);

  return {
    labels: buckets.map(bucket => resolveBucketLabel(bucket, translate)),
    workTimeSeries: hoursTypes.map(hoursType => {
      const labelKey = hoursTypeLabelKey(hoursType);
      return {
        hoursType,
        labelKey,
        label: translate(labelKey),
        hours: buckets.map(bucket => minutesToHours(bucket.workTimeByHoursType[hoursType] ?? 0))
      };
    }),
    absenceHours: buckets.map(bucket => minutesToHours(bucket.absenceMinutes)),
    absenceSeriesLabel: translate(CHART_ABSENCE_SERIES_KEY),
    includeAbsence
  };
}

export function buildTimeReportChartCalendar(
  mode: SelectedTimeReportTypeEnum,
  selectedYear: number,
  selectedMonth: number,
  selectedWeek: number,
  selectedDay: number,
  rangeStart?: Date,
  rangeEnd?: Date
): TimeReportChartCalendar {
  moment.locale('en', { week: { dow: 1 } });

  switch (mode) {
    case SelectedTimeReportTypeEnum.DAY: {
      const date = moment({ year: selectedYear, month: selectedMonth, day: selectedDay }).toDate();
      return {
        dates: [date],
        labels: [moment(date).format('D MMM YYYY')],
        dateComparison: 'day'
      };
    }
    case SelectedTimeReportTypeEnum.WEEK: {
      const startOfWeek = moment().year(selectedYear).week(selectedWeek).startOf('isoWeek');
      const dates: Date[] = [];
      for (let index = 0; index < 7; index++) {
        dates.push(startOfWeek.clone().add(index, 'days').toDate());
      }
      return {
        dates,
        labels: moment.weekdaysShort(true),
        dateComparison: 'day'
      };
    }
    case SelectedTimeReportTypeEnum.MONTH: {
      const startOfMonth = moment({ year: selectedYear, month: selectedMonth }).startOf('month');
      const daysInMonth = startOfMonth.daysInMonth();
      const dates: Date[] = [];
      const labels: string[] = [];
      for (let day = 1; day <= daysInMonth; day++) {
        dates.push(startOfMonth.clone().date(day).toDate());
        labels.push(String(day));
      }
      return { dates, labels, dateComparison: 'day' };
    }
    case SelectedTimeReportTypeEnum.YEAR: {
      const startOfYear = moment({ year: selectedYear }).startOf('year');
      const dates: Date[] = [];
      for (let month = 0; month < 12; month++) {
        dates.push(startOfYear.clone().month(month).toDate());
      }
      return {
        dates,
        labels: moment.monthsShort(),
        dateComparison: 'month'
      };
    }
    case SelectedTimeReportTypeEnum.RANGE: {
      const start = moment(rangeStart ?? new Date()).startOf('day');
      const end = moment(rangeEnd ?? start).startOf('day');
      const dates: Date[] = [];
      const labels: string[] = [];
      const cursor = start.clone();
      while (cursor.isSameOrBefore(end)) {
        dates.push(cursor.toDate());
        labels.push(cursor.format('D MMM'));
        cursor.add(1, 'day');
      }
      return { dates, labels, dateComparison: 'day' };
    }
    default:
      return { dates: [], labels: [], dateComparison: 'day' };
  }
}

function aggregateByCalendar(rows: TimeReportRowVM[], calendar: TimeReportChartCalendar): TimeReportChartBucket[] {
  return calendar.dates.map((date, index) => {
    const matching = rows.filter(row => rowMatchesCalendarDate(row, date, calendar.dateComparison));
    return rowsToBucket(
      moment(date).format(calendar.dateComparison === 'month' ? 'YYYY-MM' : 'YYYY-MM-DD'),
      calendar.labels[index] ?? '',
      undefined,
      matching
    );
  });
}

function rowMatchesCalendarDate(row: TimeReportRowVM, date: Date, comparison: 'day' | 'month'): boolean {
  if (!row.date) {
    return false;
  }
  return moment(row.date).isSame(date, comparison);
}

function groupToBucket(group: TimeReportRowGroup): TimeReportChartBucket {
  const missing = group.key === MISSING_GROUP_KEY || group.key.includes(MISSING_GROUP_KEY);
  const dateLabel = group.date ? moment(group.date).format('YYYY-MM-DD') : undefined;
  const composedLabel = [group.title, group.showDate ? dateLabel : undefined].filter(Boolean).join(' ');

  return rowsToBucket(
    group.key,
    composedLabel,
    group.titleTranslationKey ?? (missing && !composedLabel ? EMPTY_CHART_LABEL_KEY : undefined),
    group.rows
  );
}

function rowsToBucket(
  key: string,
  label: string,
  labelTranslationKey: string | undefined,
  rows: TimeReportRowVM[]
): TimeReportChartBucket {
  return {
    key,
    label,
    labelTranslationKey,
    workTimeByHoursType: sumWorkTimeByHoursType(rows),
    absenceMinutes: rows
      .filter(row => row.kind === 'Absence')
      .reduce((sum, row) => sum + durationToMinutes(row.duration), 0)
  };
}

function sumWorkTimeByHoursType(rows: TimeReportRowVM[]): Partial<Record<ChartHoursTypeSeriesKey, number>> {
  const totals: Partial<Record<ChartHoursTypeSeriesKey, number>> = {};
  for (const row of rows) {
    if (row.kind !== 'WorkTime' || !isWorkTimeReport(row.source)) {
      continue;
    }
    const durations = getWorkTimeChartDurations(row.source);
    for (const series of WORK_TIME_CHART_SERIES) {
      const minutes = durations[series];
      if (minutes === 0) {
        continue;
      }
      totals[series] = (totals[series] ?? 0) + minutes;
    }
  }
  return totals;
}

function collectPresentHoursTypes(buckets: TimeReportChartBucket[]): ChartHoursTypeSeriesKey[] {
  return WORK_TIME_CHART_SERIES.filter(hoursType =>
    buckets.some(bucket => (bucket.workTimeByHoursType[hoursType] ?? 0) > 0)
  );
}

function resolveBucketLabel(bucket: TimeReportChartBucket, translate: (key: string) => string): string {
  if (bucket.labelTranslationKey) {
    return translate(bucket.labelTranslationKey);
  }
  if (bucket.label) {
    return bucket.label;
  }
  return translate(EMPTY_CHART_LABEL_KEY);
}
